import { useState, useEffect, useCallback } from "react";
import { Download01, TrendUp01, TrendDown01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { StyledSelect } from "@/components/ui/styled-select";
import { getVatReport, downloadReportCsv, type VatReportData } from "@/api/reports";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(amount);

const currentYear = new Date().getFullYear();
const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);

const quarterOptions = [
  { label: "Q1 (Jan–Mar)", value: "1" },
  { label: "Q2 (Apr–Jun)", value: "2" },
  { label: "Q3 (Jul–Sep)", value: "3" },
  { label: "Q4 (Oct–Dec)", value: "4" },
];

const yearOptions = Array.from({ length: 5 }, (_, i) => {
  const y = currentYear - i;
  return { label: String(y), value: String(y) };
});

export default function VatReportTab() {
  const [quarter, setQuarter] = useState(String(currentQuarter));
  const [year, setYear] = useState(String(currentYear));
  const [data, setData] = useState<VatReportData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getVatReport(Number(quarter), Number(year));
      setData(result);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [quarter, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    downloadReportCsv("vat", { quarter, year }, `vat-report-Q${quarter}-${year}.csv`);
  };

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <StyledSelect label="Quarter" options={quarterOptions} value={quarter} onChange={setQuarter} />
        <StyledSelect label="Year" options={yearOptions} value={year} onChange={setYear} />
        <Button size="md" color="secondary" iconLeading={Download01} onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-text-tertiary">Loading report...</div>
      ) : !data ? (
        <div className="py-12 text-center text-text-tertiary">Failed to load report</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <SummaryCard label="Taxable Revenue" value={formatCurrency(data.totals.taxableAmount)} color="brand" />
            <SummaryCard label="VAT Collected" value={formatCurrency(data.totals.vatCollected)} color="green" />
            <SummaryCard label="Total (inc. VAT)" value={formatCurrency(data.totals.totalWithVat)} color="brand" />
            <SummaryCard label="Effective VAT Rate" value={`${data.totals.effectiveVatRate}%`} color="brand" />
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 border-l-4 border-l-[var(--color-fg-brand-primary)] shadow-[inset_4px_0_8px_-4px_rgba(21,112,239,0.3)]">
              <p className="text-xs text-text-tertiary mb-1">vs {data.comparison.previousQuarter}</p>
              <div className="flex items-center gap-2">
                {data.comparison.changePercent >= 0 ? (
                  <TrendUp01 className="size-4 text-green-600" />
                ) : (
                  <TrendDown01 className="size-4 text-red-600" />
                )}
                <p className={`text-lg font-semibold ${data.comparison.changePercent >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {data.comparison.changePercent > 0 ? "+" : ""}{data.comparison.changePercent}%
                </p>
              </div>
              <p className="text-xs text-text-tertiary mt-0.5">
                {formatCurrency(data.comparison.previousVatCollected)} VAT
              </p>
            </div>
          </div>

          {/* Bar Chart — Monthly Breakdown */}
          {data.months.length > 0 ? (
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 mb-6">
              <h3 className="text-sm font-medium text-text-primary mb-4">Monthly Breakdown</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.months}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-secondary, #e5e7eb)" />
                    <XAxis
                      dataKey="monthLabel"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => `€${v}`}
                    />
                    <Tooltip
                      formatter={(value) => formatCurrency(Number(value))}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid var(--color-border-secondary, #e5e7eb)",
                        fontSize: "13px",
                      }}
                    />
                    <Legend />
                    <Bar dataKey="taxableAmount" name="Taxable" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="vatCollected" name="VAT" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-8 text-center text-text-tertiary mb-6">
              No invoices paid in {data.quarterLabel}
            </div>
          )}

          {/* Monthly Table */}
          {data.months.length > 0 && (
            <div className="rounded-xl border border-border-secondary bg-bg-primary overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-secondary bg-bg-secondary">
                    <th className="px-6 py-3 text-left font-medium text-text-tertiary">Month</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Taxable Amount</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">VAT Collected</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Total (inc. VAT)</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((m) => (
                    <tr key={m.month} className="border-b border-border-secondary last:border-0">
                      <td className="px-6 py-4 text-text-primary font-medium">{m.monthLabel}</td>
                      <td className="px-6 py-4 text-right text-text-secondary">{formatCurrency(m.taxableAmount)}</td>
                      <td className="px-6 py-4 text-right text-green-600 font-medium">{formatCurrency(m.vatCollected)}</td>
                      <td className="px-6 py-4 text-right text-text-secondary">{formatCurrency(m.totalWithVat)}</td>
                      <td className="px-6 py-4 text-right text-text-secondary">{m.invoiceCount}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-bg-secondary font-medium">
                    <td className="px-6 py-3 text-text-primary">
                      {data.quarterLabel} Total
                    </td>
                    <td className="px-6 py-3 text-right text-text-primary">{formatCurrency(data.totals.taxableAmount)}</td>
                    <td className="px-6 py-3 text-right text-green-600">{formatCurrency(data.totals.vatCollected)}</td>
                    <td className="px-6 py-3 text-right text-text-primary">{formatCurrency(data.totals.totalWithVat)}</td>
                    <td className="px-6 py-3 text-right text-text-secondary">{data.totals.invoiceCount}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }: {
  label: string;
  value: string;
  color: "green" | "brand";
}) {
  const styles = color === "green"
    ? "border-l-green-500 shadow-[inset_4px_0_8px_-4px_rgba(34,197,94,0.4)]"
    : "border-l-[var(--color-fg-brand-primary)] shadow-[inset_4px_0_8px_-4px_rgba(21,112,239,0.3)]";

  return (
    <div className={`rounded-xl border border-border-secondary bg-bg-primary p-4 border-l-4 ${styles}`}>
      <p className="text-xs text-text-tertiary mb-1">{label}</p>
      <p className="text-lg font-semibold text-text-primary">{value}</p>
    </div>
  );
}
